import { useEffect, useRef, useState } from "react";
import type { GuideWithSteps } from "@shared/modules/guides";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Markdown } from "./markdown";

/**
 * Lecteur d'étapes partagé par la page connectée et la page publique :
 * sommaire à gauche, étape courante à droite avec précédent/suivant.
 */
export function GuideReader({ guide }: { guide: GuideWithSteps }) {
  const steps = guide.steps;
  const [index, setIndex] = useState(0);
  const contentRef = useRef<HTMLElement>(null);

  // Nouveau guide affiché : on repart de la première étape.
  useEffect(() => {
    setIndex(0);
  }, [guide.id]);

  const go = (next: number) => {
    if (next < 0 || next >= steps.length) return;
    setIndex(next);
    // Le contenu de l'étape suivante doit se lire depuis son titre, pas depuis
    // l'endroit où l'on avait fini de lire la précédente.
    contentRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const current = steps[index];
  if (!current) {
    return <div className="empty">Ce guide n'a pas encore d'étape.</div>;
  }

  return (
    <div className="guide-reader">
      <nav className="guide-reader__toc" aria-label="Étapes du guide">
        <p className="guide-reader__toc-title">Sommaire</p>
        <ol className="guide-reader__toc-list">
          {steps.map((step, i) => (
            <li key={step.id}>
              <button
                type="button"
                className={`guide-reader__toc-item${i === index ? " is-active" : ""}`}
                aria-current={i === index ? "step" : undefined}
                onClick={() => go(i)}
              >
                <span className="guide-reader__toc-num">{i + 1}</span>
                <span className="guide-reader__toc-label">{step.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <article className="guide-reader__content" ref={contentRef}>
        <p className="guide-reader__eyebrow">
          Étape {index + 1} sur {steps.length}
        </p>
        <h2 className="guide-reader__step-title">{current.title}</h2>
        <Markdown body={current.bodyMd} />
        <div className="guide-reader__nav">
          <Button variant="ghost" icon={<Icon name="chevron-left" size={16} />} disabled={index === 0} onClick={() => go(index - 1)}>
            Précédent
          </Button>
          <span className="guide-reader__nav-count">
            {index + 1} / {steps.length}
          </span>
          <Button variant="primary" disabled={index === steps.length - 1} onClick={() => go(index + 1)}>
            Suivant
            <Icon name="chevron-right" size={16} />
          </Button>
        </div>
      </article>
    </div>
  );
}
